import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import PanelHeader from "components/panelheader/panelHeader";
import { FormGroup, Input, Button, Card, CardBody, Row, Col } from "reactstrap";
import { getNoticia, saveNoticia, uploadImagenNoticia } from "services/noticiasService";
import { getConfig } from "config/config";
import { toastBigError } from "components/utils/toasts";
import Joi from "joi-browser";
import { usePageLoading } from "hooks/usePageLoading";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";

const config = getConfig();
const imagenUrl = (filename) => (filename ? config.filesUrl + "/noticias/" + filename : "");

// Fecha (Date del backend) -> valor YYYY-MM-DD para el <input type="date">.
const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};
const quillFormats = ["header", "bold", "italic", "underline", "list", "bullet", "link"];

const NoticiasForm = (props) => {
  const doneLoading = usePageLoading(props);
  let { id } = useParams();
  let location = useLocation();
  let navigate = useNavigate();
  const validateForm = true;
  const [data, setData] = React.useState({
    _id: id ? id : null,
    titulo: "",
    fecha: new Date().toISOString().slice(0, 10),
    contenido: "",
    imagen: "",
  });
  const [errors, setErrors] = React.useState([]);
  const [action, setAction] = React.useState(location.state?.action ? location.state.action : "");
  const [pendingImage, setPendingImage] = React.useState(null); // File nuevo sin subir
  const [preview, setPreview] = React.useState(""); // objectURL del File nuevo
  const fileInputRef = React.useRef();

  const readOnly = action === "C";

  const schema = {
    _id: Joi.string(),
    titulo: Joi.string()
      .label("Título")
      .required()
      .error((errors) => {
        return errors.map(() => ({ message: "Debe ingresar un título" }));
      }),
    fecha: Joi.string()
      .label("Fecha")
      .required()
      .error((errors) => {
        return errors.map(() => ({ message: "Debe ingresar una fecha" }));
      }),
    contenido: Joi.string().allow("").optional(),
    imagen: Joi.string().allow("").allow(null).optional(),
  };

  const fetchData = async (id) => {
    try {
      const record = await getNoticia(id);
      mapToViewModel(record);
    } finally {
      doneLoading();
    }
  };

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    const id = data._id;
    if (id && !id.includes("nuev")) {
      fetchData(id);
    } else {
      const cacheData = { ...data };
      delete cacheData._id;
      setData(cacheData);
      setAction("A");
      doneLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapToViewModel = (noticia) => {
    setData({
      _id: noticia._id,
      titulo: noticia.titulo || "",
      fecha: toDateInput(noticia.fecha),
      contenido: noticia.contenido || "",
      imagen: noticia.imagen || "",
    });
  };

  const handleChange = (event) => {
    const input = event.currentTarget;
    const cacheErrors = { ...errors };
    const cacheData = { ...data };
    if (validateForm) {
      const errorMessage = validateProperty(input);
      if (errorMessage) {
        cacheErrors[input.name] = errorMessage;
      } else delete cacheErrors[input.name];
    } else delete cacheErrors[input.name];
    cacheData[input.name] = input.value;
    setData(cacheData);
    setErrors(cacheErrors);
  };

  const handleContenidoChange = (value) => {
    setData({ ...data, contenido: value });
  };

  const handleImageSelect = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setPendingImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const quitarImagen = () => {
    setPendingImage(null);
    setPreview("");
    setData({ ...data, imagen: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validate = () => {
    let errors = {};
    const options = { abortEarly: false };
    if (!validateForm) return null;
    let { error } = Joi.validate(data, schema, options);
    if (!error) return null;
    error.details.map((validationError) => {
      return (errors[validationError.path[0]] = validationError.message);
    });
    return errors;
  };

  const validateProperty = (input) => {
    const { name, value } = input;
    const object = { [name]: value };
    const checkSchema = { [name]: schema[name] };
    const { error } = Joi.validate(object, checkSchema);
    return error ? error.details[0].message : null;
  };

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    const checkErrors = validate();
    if (checkErrors) {
      setErrors(checkErrors);
      const first = Object.values(checkErrors)[0];
      if (first) toastBigError(first, 2500);
      return;
    }
    doSubmit();
  };

  const doSubmit = async () => {
    const response = await saveNoticia(data);
    // saveNoticia devuelve el response completo; el _id sale del record.
    let recordId = data._id;
    if (!recordId && response && response.data && response.data.record) {
      recordId = response.data.record._id;
    }
    if (pendingImage && recordId) {
      await uploadImagenNoticia(recordId, pendingImage, data.imagen);
    }
    navigate(-1);
  };

  const goBack = () => {
    navigate(-1);
  };

  const imagenActual = preview || imagenUrl(data.imagen);

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content">
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <Row>
                  <Col md="4">
                    <FormGroup className={`has-label ` + (errors["fecha"] ? "has-danger" : "")}>
                      <label>Fecha</label>
                      <Input
                        id="fecha"
                        name="fecha"
                        type="date"
                        disabled={readOnly}
                        value={data.fecha}
                        onChange={(e) => handleChange(e)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="8">
                    <FormGroup className={`has-label ` + (errors["titulo"] ? "has-danger" : "")}>
                      <label>Título</label>
                      <Input
                        id="titulo"
                        name="titulo"
                        autoComplete="off"
                        disabled={readOnly}
                        value={data.titulo}
                        onChange={(e) => handleChange(e)}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup className="has-label">
                  <label>Imagen</label>
                  {imagenActual ? (
                    <div className="noticia-imagen-preview">
                      <img src={imagenActual} alt="Imagen de la noticia" />
                    </div>
                  ) : (
                    <p className="text-muted" style={{ fontSize: "0.8125rem" }}>
                      {readOnly ? "Sin imagen." : "No hay imagen cargada."}
                    </p>
                  )}
                  {!readOnly && (
                    <div className="d-flex" style={{ gap: "0.5rem", marginTop: "0.5rem" }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleImageSelect}
                      />
                      <Button className="btn-arhock btn-sm my-0" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                        {imagenActual ? "Cambiar imagen" : "Subir imagen"}
                      </Button>
                      {imagenActual && (
                        <Button className="btn-cancelar btn-sm my-0" onClick={quitarImagen}>
                          Quitar imagen
                        </Button>
                      )}
                    </div>
                  )}
                </FormGroup>

                <FormGroup className="has-label">
                  <label>Contenido</label>
                  {readOnly ? (
                    <div
                      className="noticia-contenido-view"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.contenido || "") }}
                    />
                  ) : (
                    <ReactQuill
                      theme="snow"
                      value={data.contenido}
                      onChange={handleContenidoChange}
                      modules={quillModules}
                      formats={quillFormats}
                    />
                  )}
                </FormGroup>

                <Row>
                  <Col md="12">
                    <Button className="pull-right btn-cancelar" onClick={goBack}>
                      {readOnly ? "Volver" : "Cancelar"}
                    </Button>
                    {!readOnly && (
                      <Button className="pull-right btn-arhock" onClick={handleSubmit}>
                        Aceptar
                      </Button>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default NoticiasForm;
